import { useTheme } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import DialogActions from "src/containers/DialogActions/DialogActions";
import FilterByDays from "src/containers/FilterByDays/FilterByDays";
import useChangeDays from "src/containers/FilterByDays/useChangeDays/useChangeDays";
import LoadingOverlay from "src/containers/LoadingOverlay/LoadingOverlay";
import {
  AnomalyTypesQuickfilter,
  useAnomalyTypesQuickFilter,
} from "src/containers/Quickfilters/AnomalyTypesQuickfilter";
import {
  CurrentEmployeesQuickfilter,
  useCurrentEmployeesQuickFilter,
} from "src/containers/Quickfilters/CurrentEmployeesQuickfilter";
import { useEmployeeGroupQuickFilter } from "src/containers/Quickfilters/EmployeeGroupQuickfilter";
import EmployeeGroupQuickfilter from "src/containers/Quickfilters/EmployeeGroupQuickfilter/EmployeeGroupQuickfilter";
import { EmployeesQuickfilter } from "src/containers/Quickfilters/EmployeesQuickfilter/EmployeesQuickfilter";
import { useEmployeesQuickFilter } from "src/containers/Quickfilters/EmployeesQuickfilter/useEmployeesQuickFilter";
import { LegalEntityQuickfilter, useLegalEntityQuickFilter } from "src/containers/Quickfilters/LegalEntityQuickfilter";
import QuickfiltersContainer from "src/containers/Quickfilters/QuickfiltersContainer/QuickfiltersContainer";
import { EmployeeRef } from "src/models/employees";
import { AnomalyTrackingDTO } from "src/models/tna/tna-anomalies/anomaly-tracking-dto";
import { AnomalyTrackingFilter } from "src/models/tna/tna-anomalies/anomaly-tracking-filter";
import Table from "src/reusable-components/Table/Table";
import { AnomaliesTrackingByEmployees } from "src/services/tna-anomalies/anomalies-tracking-by-employees-api.service";
import { FT_TNAHT } from "src/utils/constants/filter-tables-constants";
import { intlFormatDateShort } from "src/utils/time-utils";
import { v4 as uuidv4 } from "uuid";

interface Props {
  handleNotifyParent: () => void;
  tableHeight: number;
  handleClose?: () => void;
}

export interface State {
  loading: boolean;
  error: string;
  employee: EmployeeRef | null;
  lastNotifiedOn: Date | null;
  lastTimesheetSignOff: Date | null;
  allEmployees: boolean;
  employeeQfId: string;
  showResolvedAnomalies: boolean;
}

const MultipleEmployeesAnomaliesHistoryTable = ({ handleNotifyParent, tableHeight, handleClose }: Props) => {
  const { t } = useTranslation();
  const tRP = "components.my_team_breaks.anomalies_history_table"; // tRP - translation root path
  const theme = useTheme();

  const {
    legalEntitiesQuickFilter,
    setLegalEntitiesQuickFilter,
    selectedLegalEntity,
    legalEntityQFIsLoading,
    allLegalEntities,
  } = useLegalEntityQuickFilter(true);

  const { anomalyTypesQuickFilter, setAnomalyTypesQuickFilter, allAnomalyTypes, anomalyTypeQFIsLoading } =
    useAnomalyTypesQuickFilter(true);

  const { employeeGroupQuickFilter, setEmployeeGroupQuickFilter } = useEmployeeGroupQuickFilter();

  const { onlyCurrentEmployeesQuickFilter, setOnlyCurrentEmployeesQuickFilter } = useCurrentEmployeesQuickFilter();

  const { daysQuickFilter, setDaysQuickFilter, selectedDays } = useChangeDays();

  const { employeesQuickFilter, setEmployeesQuickFilter, allEmployees, employeeQFIsLoading } = useEmployeesQuickFilter(
    true,
    employeeGroupQuickFilter.employeeGroups?.employeeGroupIds ?? [],
    legalEntitiesQuickFilter.legalEntities?.legalEntityIds ?? [],
    !legalEntityQFIsLoading
  );

  // const hasAccess = useAuthorization([
  //   AUTHORIZATIONS.EmployeesAccess.CreateEmployees,
  //   AUTHORIZATIONS.EmployeesAccess.ConfigureEmployeeGroupParameters,
  //   AUTHORIZATIONS.LegalEntityAccess.ProcessPayroll,
  // ]);

  const [state, setState] = useState<State>({
    loading: false,
    error: "",
    employee: null,
    lastNotifiedOn: null,
    lastTimesheetSignOff: null,
    allEmployees: true,
    employeeQfId: uuidv4(),
    showResolvedAnomalies: true,
  });

  // const allowedToViewOnly = useAuthorization([AUTHORIZATIONS.EmployeesAccess.ViewNonSalaryAndCompensationData]);

  // const selectedRowsDataRef = useRef<AnomalyTrackingDTO[]>([]);

  const columns: GridColDef<AnomalyTrackingDTO>[] = [
    {
      field: "lastNotifiedOn",
      headerName: t("common_words.last_notified_on"),
      headerAlign: "center",
      sortable: true,
      disableColumnMenu: true,
      valueGetter: ({ row }) => (row.lastNotifiedOn ? intlFormatDateShort(new Date(row.lastNotifiedOn)) : ""),
      flex: 1,
      align: "center",
    },
    {
      field: "anomalyDescription",
      headerName: t("common_words.anomaly"),
      headerAlign: "center",
      sortable: false,
      disableColumnMenu: true,
      valueGetter: ({ row }) => row?.anomalyType?.description ?? "",
      flex: 1.5,
      align: "left",
    },
    {
      field: "anomalyDate",
      headerName: t(`${tRP}.fields.date_of_anomaly`),
      headerAlign: "center",
      sortable: true,
      disableColumnMenu: true,
      valueGetter: ({ row }) => intlFormatDateShort(new Date(row?.year, row?.month - 1, row?.day)) ?? "",
      flex: 1,
      align: "center",
    },
    {
      field: "comment",
      headerName: t(`${tRP}.comment_notification`),
      headerAlign: "center",
      sortable: false,
      disableColumnMenu: true,
      renderCell: ({ row }) =>
        // <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
        //   <Typography
        //     variant="body2"
        //     style={{ flex: 1 }}
        //   >
        //     {row?.comment ?? ""}
        //   </Typography>
        //   {(!row?.comment || row?.comment === "") && (
        //     <AddEditComment
        //       item={row}
        //       effect={row?.comment ? "edit" : "add"}
        //       // handleNotifyParent={forceUpdate}
        //       disabled={!hasAccess || allowedToViewOnly}
        //       viewMode={allowedToViewOnly}
        //     >
        //       <IconButton
        //         size="small"
        //         title="Edit comment"
        //       >
        //         <FontAwesomeIcon
        //           icon={faPenToSquare}
        //           color={theme.palette.primary.contrastText}
        //         />
        //       </IconButton>
        //     </AddEditComment>
        //   )}
        // </div>
        row?.comment ?? "",
      flex: 3,
      align: "left",
    },
  ];

  const quickFilters = React.useMemo(
    () => (
      <QuickfiltersContainer>
        <LegalEntityQuickfilter
          quickFilters={legalEntitiesQuickFilter}
          handleChangeQuickFilters={setLegalEntitiesQuickFilter}
          allLegalEntitiesOption
          allLegalEntities={allLegalEntities}
        />
        <EmployeeGroupQuickfilter
          quickFilters={employeeGroupQuickFilter}
          handleChangeQuickFilters={setEmployeeGroupQuickFilter}
          allEmployeeGroupsOption
        />
        <CurrentEmployeesQuickfilter
          quickFilters={onlyCurrentEmployeesQuickFilter}
          handleChangeQuickFilters={setOnlyCurrentEmployeesQuickFilter}
        />
        {/* <Grid
        style={{
          borderRadius: "12px",
          backgroundColor: theme.palette.background.paper,
          display: "flex",
          gap: "10px",
          padding: "0px 10px",
          alignItems: "center",
        }}
      >
        <Grid>
          <Typography color={theme.palette.secondary.light}>{"Employee"}</Typography>
        </Grid>
        <Divider
          flexItem
          orientation={"vertical"}
        />
        <Grid>
          <AutocompleteDropdown
            key={state.employeeQfId}
            field="employee"
            childField="legalEntity"
            childFieldProp="name"
            serviceApi={new EmployeesApiService()}
            apiLoadItemsPath={undefined}
            handleChange={(_, employee) =>
              setState((state) => ({
                ...state,
                allEmployees: employee?.id === allAutocompliteOption,
                employee: employee?.id === allAutocompliteOption ? null : employee,
                tnAEmployeeActualConfig: null,
                items: [],
              }))
            }
            inputVariant="standard"
            selectedValue={state.employee}
            inputStyle={{
              minWidth: "130px",
              paddingRight: "40px",
              margin: "5px 0px",
              padding: "1px 0",
              fontSize: theme.typography.body1.fontSize,
            }}
            withAllOption
            allOptionAsDefault
            sxProps={{ minWidth: "130px" }}
            propsFilter={
              {
                employeeGroupIds: employeeGroupQuickFilter.employeeGroups?.employeeGroupIds ?? [],
                legalEntityIds: legalEntitiesQuickFilter.legalEntities?.legalEntityIds ?? [],
              } as Partial<EmployeesFilter>
            }
          />
        </Grid>
      </Grid> */}
        <EmployeesQuickfilter
          quickFilters={employeesQuickFilter}
          handleChangeQuickFilters={setEmployeesQuickFilter}
          allEmployeesOption
          allEmployees={allEmployees}
          isMultiSelect
        />
        <AnomalyTypesQuickfilter
          quickFilters={anomalyTypesQuickFilter}
          handleChangeQuickFilters={setAnomalyTypesQuickFilter}
          allAnomalyTypesOption
          allAnomalyTypes={allAnomalyTypes}
          isMultiSelect
        />
        {/* <Grid
        style={{
          borderRadius: "12px",
          backgroundColor: theme.palette.background.paper,
          display: "flex",
          gap: "10px",
          padding: "0px 10px",
          alignItems: "center",
        }}
      > */}
        {/*<FormControlLabel*/}
        {/*  control={*/}
        {/*    <Switch*/}
        {/*      checked={state.showResolvedAnomalies}*/}
        {/*      onChange={(_, checked) =>*/}
        {/*        setState((prev) => ({*/}
        {/*          ...prev,*/}
        {/*          showResolvedAnomalies: checked,*/}
        {/*        }))*/}
        {/*      }*/}
        {/*      size="small"*/}
        {/*    />*/}
        {/*  }*/}
        {/*  label={*/}
        {/*    <Typography*/}
        {/*      variant="body2"*/}
        {/*      color={theme.palette.secondary.light}*/}
        {/*    >*/}
        {/*      {t("common_words.show_resolved_anomalies")}*/}
        {/*    </Typography>*/}
        {/*  }*/}
        {/*/>*/}
        {/* <EmployeesQuickfilter
          quickFilters={employeesQuickFilter}
          handleChangeQuickFilters={setEmployeesQuickFilter}
          allEmployeesOption
          allEmployees={allEmployees}
          isMultiSelect
        /> */}
        <FilterByDays
          quickFilters={daysQuickFilter}
          handleChangeQuickFilters={setDaysQuickFilter}
          filterColor="filter"
          withMaxDate={false}
          maxSelectableDays={31}
        />
        {/* </Grid> */}
      </QuickfiltersContainer>
    ),
    [
      allAnomalyTypes,
      allEmployees,
      allLegalEntities,
      anomalyTypesQuickFilter,
      daysQuickFilter,
      employeeGroupQuickFilter,
      employeesQuickFilter,
      legalEntitiesQuickFilter,
      onlyCurrentEmployeesQuickFilter,
      setAnomalyTypesQuickFilter,
      setDaysQuickFilter,
      setEmployeeGroupQuickFilter,
      setEmployeesQuickFilter,
      setLegalEntitiesQuickFilter,
      setOnlyCurrentEmployeesQuickFilter,
    ]
  );

  const propsFilter = React.useMemo(
    () =>
      ({
        employeesIds: employeesQuickFilter.employees?.employeeIds || [],
        legalEntityIds: selectedLegalEntity?.id ? [selectedLegalEntity.id] : [],
        employeeGroupIds: employeeGroupQuickFilter.employeeGroups?.employeeGroupIds || [],
        isResolved: state.showResolvedAnomalies,
        days: selectedDays,
        typeIds: anomalyTypesQuickFilter.anomalyTypes?.anomalyTypeIds || [],
        isNotified: true,
        isAbnormalPresence: false,
      } as AnomalyTrackingFilter),
    [
      employeesQuickFilter.employees?.employeeIds,
      selectedLegalEntity?.id,
      employeeGroupQuickFilter.employeeGroups?.employeeGroupIds,
      state.showResolvedAnomalies,
      selectedDays,
      anomalyTypesQuickFilter.anomalyTypes?.anomalyTypeIds,
    ]
  );

  return (
    <LoadingOverlay loading={state.loading || legalEntityQFIsLoading || employeeQFIsLoading || anomalyTypeQFIsLoading}>
      {/* {employee?.id && (
        <Grid
          container
          sx={{ alignItems: "center" }}
          spacing={2}
        >
          <Grid
            item
            xs={6}
          >
            <ConcatenatedCell
              aligned={"left"}
              FirstElement={
                <Grid>
                  <Typography>{employee?.name}</Typography>
                  {employee?.birthDate && (
                    <Typography variant={"body2"}>
                      <FontAwesomeIcon
                        icon={faBirthdayCake}
                        size="xs"
                      />{" "}
                      &nbsp;
                      {intlFormatDateShort(employee?.birthDate)}
                    </Typography>
                  )}
                </Grid>
              }
              SecondElement={
                <Grid>
                  <Typography
                    style={{ color: theme.palette.text.disabled }}
                    variant={"body2"}
                    title={employee?.legalEntity?.name}
                  >
                    {employee?.legalEntity?.code ?? employee?.legalEntity?.name}
                  </Typography>
                </Grid>
              }
            />
          </Grid>
          <Grid
            item
            xs={6}
            sx={{ textAlign: "right" }}
          >
            <Typography
              variant="body2"
              style={{ color: theme.palette.text.secondary }}
            >
              Last anomaly notification sent on:{" "}
              {state.lastNotifiedOn ? intlFormatDateShort(state.lastNotifiedOn) : "N/A"}
            </Typography>
            <Typography
              variant="body2"
              style={{ color: theme.palette.text.secondary }}
            >
              Last signoff date: {state.lastTimesheetSignOff ? intlFormatDateShort(state.lastTimesheetSignOff) : "N/A"}
            </Typography>
          </Grid>
        </Grid>
      )} */}
      <Table
        hideRemoveIcon
        translationRootPath={tRP}
        displayTitle={false}
        columns={columns}
        allowEdit={false}
        readOnly
        defaultPageSize={10}
        tableHeight={tableHeight}
        serviceApi={new AnomaliesTrackingByEmployees()}
        propsFilter={propsFilter}
        quickFilters={quickFilters}
        filter={FT_TNAHT}
      />
      <DialogActions
        handleCancel={() => {
          setState((prev) => ({ ...prev, error: "" }));
          handleClose?.();
        }}
        handleSave={handleClose}
        disabledCancelButton={state.loading}
        customSaveName={t(`${tRP}.close`)}
      />
    </LoadingOverlay>
  );
};

export default React.memo(MultipleEmployeesAnomaliesHistoryTable);
